import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* Sign-up flows (volunteer slot booking). The backend addresses one by its
   generated `signupId` string — not the Mongo _id — on edit, status and delete.

   Shape on the wire: dates[].timeWindows[] with { start, end, spots }; the UI
   has always called those `windows` with a `capacity`, so translate both ways
   and leave the rest of the module untouched.

   `kind` picks one of three builders (see SIGNUP_KINDS): a By-date ('open')
   window is { anyTime: true, start: null, end: null, spots } and its date may
   carry a `label`; a Supplies ('items') sign-up has items[] { name, qty } and
   dates: []. `acceptsDonations` adds the Donate button. */

const bool = (v) => v === true || v === 'true';

function normalizeSignup(s) {
  const kind = ['slots', 'open', 'items'].includes(s?.kind) ? s.kind : 'slots';
  const dates = (s?.dates || []).map((d, di) => ({
    /* Mongo gives each date/window its own _id — good, stable React keys */
    id: d?._id || `d${di}`,
    /* stored as a Date, so it comes back as a full ISO timestamp */
    date: String(d?.date || '').slice(0, 10),
    label: d?.label || '',
    windows: (d?.timeWindows || []).map((w, wi) => ({
      id: w?._id || `d${di}w${wi}`,
      start: w?.start || '',
      end: w?.end || '',
      anyTime: bool(w?.anyTime) || kind === 'open',
      /* null/absent spots = no limit */
      capacity: w?.spots == null || w?.spots === '' ? null : Number(w.spots) || null,
      /* parents who took this window from the public sign-up page; the API
         stores their phone as E.164 */
      signed: (w?.signups || []).map((e, ei) => ({
        id: e?._id || `d${di}w${wi}s${ei}`,
        name: e?.name || '',
        contact: e?.phone || '',
        parentId: e?.parentId || null,
      })),
    })),
  }));
  const items = (s?.items || []).map((it, i) => ({
    id: it?._id || `i${i}`,
    name: it?.name || '',
    qty: Number(it?.qty) || 1,
    /* claims aren't stored yet either */
    claimed: [],
  }));
  return {
    id: s?.signupId || s?._id || '',
    kind,
    title: s?.title || '',
    desc: s?.description || '',
    image: s?.photo || null,
    eventId: s?.eventId || null,
    networkId: s?.networkId || '',
    /* status true = published, i.e. sent to parents */
    status: bool(s?.status),
    acceptsDonations: bool(s?.acceptsDonations),
    /* the short code the backend minted at create time — the public sign-up
       link (signupLinkUrl) is built from it */
    shortCode: s?.shortCode || '',
    dates,
    items,
  };
}

/* UI windows → the API's timeWindows. `label` and `anyTime` only go out when
   set, so a plain time-slot sign-up is sent exactly as it always was. A blank
   Spots field (capacity null) goes out as spots: null — no limit. */
const toApiSpots = (capacity) => (capacity == null ? null : Number(capacity) || 1);
const toApiDates = (dates) =>
  (dates || []).map((d) => ({
    date: d.date,
    ...(d.label ? { label: d.label } : {}),
    timeWindows: (d.windows || []).map((w) =>
      w.anyTime
        ? { anyTime: true, start: null, end: null, spots: toApiSpots(w.capacity) }
        : { start: w.start, end: w.end, spots: toApiSpots(w.capacity) }
    ),
  }));

const toApiItems = (items) => (items || []).map((i) => ({ name: i.name, qty: Number(i.qty) || 1 }));

/* A photo is either an uploaded File (multipart) or an http(s) URL string — the
   backend validates the string as a URI, so a data: preview is dropped rather
   than sent (the upload itself carries that image). An empty string clears it. */
const appendPhoto = (fd, { photo, photoFile }) => {
  if (photoFile) {
    fd.append('photo', photoFile);
    return;
  }
  if (typeof photo !== 'string') return;
  if (photo === '' || /^https?:\/\//i.test(photo)) fd.append('photo', photo);
};

const signupsService = {
  /* Paginated, newest first, scoped to one network (class). */
  list: async (networkId, { page = 1, limit = 10, status, search = '' } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.SIGNUP_FLOWS_BY_NETWORK(networkId), {
      params: {
        page,
        limit,
        ...(status === undefined ? {} : { status }),
        ...(search ? { search } : {}),
      },
    });
    const data = res?.data ?? res;
    const rawList =
      data?.signupFlows ||
      data?.signups ||
      data?.docs ||
      data?.items ||
      (Array.isArray(data?.data) ? data.data : null) ||
      (Array.isArray(data) ? data : []);
    const total = Number(data?.totalCount ?? data?.total ?? data?.count ?? rawList.length);
    const totalPages = Number(data?.totalPages ?? Math.max(1, Math.ceil(total / limit)));
    return { signups: rawList.map(normalizeSignup), total, totalPages };
  },

  /* Multipart create — eventId is required by the backend, so every sign-up
     hangs off a calendar event. New flows start as drafts (status false) and
     are published from the row's "Send to parents". The API rejects fields it
     doesn't know, so kind/acceptsDonations/items stay off the wire at their
     defaults ('slots', false, none) and a time-slot sign-up keeps working on
     an API that predates them. */
  create: ({ networkId, eventId, kind = 'slots', title, description, dates, items, acceptsDonations, photo, photoFile, status }) => {
    const fd = new FormData();
    fd.append('networkId', networkId);
    fd.append('eventId', eventId);
    if (kind !== 'slots') fd.append('kind', kind);
    fd.append('title', title);
    fd.append('description', description || '');
    fd.append('dates', JSON.stringify(toApiDates(dates)));
    if (kind === 'items') fd.append('items', JSON.stringify(toApiItems(items)));
    if (acceptsDonations) fd.append('acceptsDonations', 'true');
    if (status !== undefined) fd.append('status', String(status));
    appendPhoto(fd, { photo, photoFile });
    return apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.SIGNUP_FLOW, fd);
  },

  /* Partial update — only the fields passed are touched. `dates` and `items`
     are sent in UI shape and converted here, same as create. */
  update: ({ signupId, eventId, kind, title, description, dates, items, acceptsDonations, photo, photoFile, status }) => {
    const fd = new FormData();
    fd.append('signupId', signupId);
    if (eventId !== undefined) fd.append('eventId', eventId);
    if (kind !== undefined) fd.append('kind', kind);
    if (title !== undefined) fd.append('title', title);
    if (description !== undefined) fd.append('description', description);
    if (dates !== undefined) fd.append('dates', JSON.stringify(toApiDates(dates)));
    if (items !== undefined) fd.append('items', JSON.stringify(toApiItems(items)));
    if (acceptsDonations !== undefined) fd.append('acceptsDonations', String(acceptsDonations));
    if (status !== undefined) fd.append('status', String(status));
    appendPhoto(fd, { photo, photoFile });
    return apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.SIGNUP_FLOW, fd);
  },

  /* status true = published/sent to parents, false = back to draft. */
  setStatus: (signupId, status) =>
    apiClient.patch(ENDPOINTS.ROOM_PARENT_ADMIN.SIGNUP_FLOW_STATUS, { signupId, status }),

  remove: (signupId) => apiClient.delete(ENDPOINTS.ROOM_PARENT_ADMIN.SIGNUP_FLOW_BY_ID(signupId)),
};

export default signupsService;
